import { Logger } from "utils/logger";

type TStorageType = "local" | "session";

interface IStorageOptions {
  type?: TStorageType;
  prefix?: string;
}

interface IStorageInstance {
  clear: () => void;
  getItem: (key: string) => unknown;
  lang: string;
  removeItem: (key: string) => void;
  setItem: (key: string, value: unknown) => Promise<boolean>;
}

const createStorage = (options: IStorageOptions = {}): IStorageInstance => {
  const storage =
    options.type === "session" ? window.sessionStorage : window.localStorage;
  const prefix = options.prefix ?? "";

  function getKey(key: string): string {
    return `${prefix}${key}`;
  }

  function clearOldItems(): void {
    try {
      // Get all keys with our prefix
      const keys = Object.keys(storage)
        .filter((key): boolean => key.startsWith(prefix))
        .sort();

      // Remove oldest items (first 20% of items)
      const CLEAR_PERCENTAGE = 0.2;
      const itemsToRemove = Math.ceil(keys.length * CLEAR_PERCENTAGE);

      keys.slice(0, itemsToRemove).forEach((key): void => {
        storage.removeItem(key);
      });
    } catch (error) {
      Logger.error("Error clearing old items", error);
    }
  }

  async function setItem(key: string, value: unknown): Promise<boolean> {
    return new Promise((resolve): void => {
      try {
        const serializedValue = JSON.stringify(value);
        storage.setItem(getKey(key), serializedValue);
        resolve(true);
      } catch (error) {
        if (
          error instanceof DOMException &&
          (error.name === "QuotaExceededError" ||
            error.name === "NS_ERROR_DOM_QUOTA_REACHED")
        ) {
          clearOldItems();

          // Retry once after clearing
          try {
            const serializedValue = JSON.stringify(value);
            storage.setItem(getKey(key), serializedValue);
            resolve(true);
          } catch (retryError) {
            Logger.error(
              "Failed to store item even after clearing old items",
              retryError,
            );
            resolve(false);
          }
        } else {
          Logger.error("Error storing item", error);
          resolve(false);
        }
      }
    });
  }

  function getItem(key: string): unknown {
    try {
      const item = storage.getItem(getKey(key));
      if (item === null) return null;

      return JSON.parse(item);
    } catch (error) {
      Logger.error("Error retrieving item", error);

      return null;
    }
  }

  function removeItem(key: string): void {
    try {
      storage.removeItem(getKey(key));
    } catch (error) {
      Logger.error("Error removing item", error);
    }
  }

  function clear(): void {
    try {
      if (prefix) {
        Object.keys(storage).forEach((key): void => {
          if (key.startsWith(prefix)) storage.removeItem(key);
        });
      } else {
        storage.clear();
      }
    } catch (error) {
      Logger.error("Error clearing storage", error);
    }
  }

  return {
    clear,
    getItem,
    lang: getItem("lang") as string,
    removeItem,
    setItem,
  };
};

const localStorage = createStorage({ type: "local" });
const sessionStorage = createStorage({ type: "session" });

export type { IStorageInstance, TStorageType };
export { createStorage, localStorage, sessionStorage };
